import { useCallback, useEffect } from 'react';
import { Location, useBlocker } from 'react-router-dom';

type UseFormDirtyStateOptions = {
  isDefaultValue: boolean;
  isEditMode: boolean;
};

export function useFormDirtyState({ isDefaultValue, isEditMode }: UseFormDirtyStateOptions) {
  const isDirty = isEditMode && !isDefaultValue;
  const isBlocked = useCallback(
    ({ currentLocation, nextLocation }: { currentLocation: Location; nextLocation: Location }) =>
      isDirty &&
      currentLocation.pathname !== nextLocation.pathname &&
      !nextLocation.pathname.endsWith('/copy'),
    [isDirty]
  );
  const blocker = useBlocker(isBlocked);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const handleLeaveWithoutSaving = useCallback(() => blocker.proceed?.(), [blocker]);
  const handleKeepEditing = useCallback(() => blocker.reset?.(), [blocker]);

  return {
    isDirty,
    showUnsavedChangesModal: blocker.state === 'blocked',
    handleLeaveWithoutSaving,
    handleKeepEditing,
  };
}
